import React, { useCallback, useEffect, useState } from 'react';
import { addHours, intervalToDuration, isAfter } from 'date-fns/esm';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Booking from '../../../core/types/model/Booking';
import { Color } from '../../../core/enums/Color';
import { BookingStatus } from '../../../core/enums/BookingStatus';
import { Size } from '../../../core/enums/Size';
import { iconSize } from '../booking-status-badge/BookingStatusBadge';
import { generateEntityGetActions } from '../../../core/state/entity/generateEntityActions';
import { selectProjectSettings } from '../../../core/state/bookingProjectSettings/selectors';
import EntityName from '../../../core/enums/EntityName';
import { isMobileScreenSize } from '../../../core/utils/isMobileScreenSize';

import Badge, { BadgeSize } from '../badge/Badge';
import Icon from '../../../components/icon/Icon';

interface Props {
  booking: Booking;
  size?: BadgeSize;
  dispatchStatusUpdate?: boolean;
  withCountdownText?: boolean;
  isShort?: boolean;
  expanded?: boolean;
  labelSize?: BadgeSize;
}

const TIME_EXPIRED = '00:00:00';

const PendingBookingCountdown: React.FC<Props> = ({
  booking,
  size = Size.Medium,
  dispatchStatusUpdate = true,
  withCountdownText = false,
  isShort = false,
  expanded,
  labelSize
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    bookingProjectSettings: { automaticConfirmationTime }
  } = useSelector(selectProjectSettings);

  const isMobileScreen = isMobileScreenSize();

  const calculateTimeRemaining = useCallback(
    (isShortFormat?: true): string => {
      const createdDate = new Date(booking.createdOn.replace(/-/g, '/'));
      const autoApproveDate = addHours(createdDate, automaticConfirmationTime + 1);
      const currentDate = new Date();

      if (isAfter(currentDate, autoApproveDate)) {
        return TIME_EXPIRED;
      }

      const duration = intervalToDuration({
        start: currentDate,
        end: autoApproveDate
      });
      const days = `${duration.days?.toString().padStart(2, '0') ?? '00'}`;
      const hours = `${duration.hours?.toString().padStart(2, '0') ?? '00'}`;
      const minutes = `${duration.minutes?.toString().padStart(2, '0') ?? '00'}`;

      if (expanded && !isShortFormat) {
        if (days !== '00') {
          return `${days}:${hours}:${minutes}`;
        }
        return hours !== '00' ? `${hours}:${minutes}` : `${minutes}`;
      }

      if (days !== '00') {
        return `${days} ${t('common.days')} ${hours} ${t('common.hours')} ${minutes} ${t('common.minutes')}`;
      }
      if (hours !== '00') {
        return `${hours} ${t('common.hours')} ${minutes} ${t('common.minutes')}`;
      }
      return `${minutes} ${t('common.minutes')}`;
    },
    [automaticConfirmationTime, booking.createdOn, t, expanded]
  );

  const [label, setLabel] = useState<string>(calculateTimeRemaining());

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLabel(calculateTimeRemaining());
    }, 1000 * 60);

    return () => clearTimeout(timeout);
  }, [calculateTimeRemaining]);

  useEffect(() => {
    if (dispatchStatusUpdate && label === TIME_EXPIRED) {
      const Actions = generateEntityGetActions(EntityName.Booking);
      dispatch(
        Actions.getSuccess({
          ...booking,
          confirmationStatus: BookingStatus.Open
        })
      );
    }
  }, [booking, dispatch, dispatchStatusUpdate, label]);

  if (booking.confirmationStatus !== BookingStatus.Pending) {
    return <></>;
  }
  const currentLabel = isShort ? '' : label;

  return (
    <Badge
      icon={withCountdownText ? null : <Icon icon="stopwatch" color={Color.Orange} size={iconSize[size]} svg />}
      color={Color.Orange}
      size={size}
      label={
        withCountdownText ? (
          <div>
            <span className={isMobileScreen ? 'd-flex flex-column' : ''}>{t('booking.status.countdown')}: </span>
            <span>{label}</span>
          </div>
        ) : (
          currentLabel
        )
      }
      withTransparentIcon
      tooltip={calculateTimeRemaining(true)}
      labelSize={labelSize}
    />
  );
};

export default PendingBookingCountdown;
